import React from 'react';
import { motion } from 'framer-motion';
import * as Icons from 'lucide-react';

const WhyChooseSection = ({ features }) => {
    if (!features || features.length === 0) return null;

    // Helper to get Icon component
    const getIcon = (iconName) => {
        const Icon = Icons[iconName] || Icons.CheckCircle;
        return <Icon size={32} />;
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1]
            }
        }
    };

    return (
        <section className="py-20 md:py-32 bg-[#0a221a] text-white relative overflow-hidden">
            {/* Background Pattern - subtle dots */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
                <div
                    className="absolute left-0 top-0 w-full h-full"
                    style={{
                        backgroundImage: 'radial-gradient(rgba(206, 255, 26, 0.2) 1px, transparent 1px)',
                        backgroundSize: '30px 30px'
                    }}
                ></div>
            </div>

            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-agro-lime/20 to-transparent"></div>

            <div className="container mx-auto px-6 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-24">
                    <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-agro-lime font-sans uppercase tracking-[0.3em] text-xs mb-4 font-black"
                    >
                        Why Choose Us
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-6xl font-serif text-white leading-tight"
                    >
                        Setting the <span className="text-agro-lime italic">Gold Standard</span> in Poultry
                    </motion.h2>
                </div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
                >
                    {features.map((feature, index) => (
                        <motion.div
                            key={feature.id || index}
                            variants={itemVariants}
                            whileHover={{ y: -10 }}
                            className="group bg-white/5 backdrop-blur-md p-8 md:p-10 rounded-[2rem] border border-white/10 hover:border-agro-lime/30 hover:bg-white/10 transition-all duration-500 shadow-2xl"
                        >
                            <div className="w-16 h-16 bg-agro-lime/10 rounded-2xl flex items-center justify-center mb-8 text-agro-lime group-hover:bg-agro-lime group-hover:text-agro-green-dark transition-all duration-500 transform group-hover:rotate-[15deg]">
                                {getIcon(feature.icon)}
                            </div>
                            <h3 className="text-2xl font-serif text-white mb-4 group-hover:text-agro-lime transition-colors">
                                {feature.title}
                            </h3>
                            <p className="text-gray-400 font-sans leading-relaxed group-hover:text-gray-200 transition-colors">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default WhyChooseSection;
